import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Box,
  Paper,
  MenuItem,
  Select,
  Button,
  FormHelperText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ─── Inject global styles ─────────────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap');

    :root {
      --navy:      #0B1F40;
      --navy-mid:  #132d5a;
      --gold:      #C9A84C;
      --gold-lt:   #e8c96e;
      --offwhite:  #F7F5F0;
      --surface:   #FFFFFF;
      --border:    #E2DDD4;
      --muted:     #7A7267;
      --text:      #1A1714;
      --error:     #C0392B;
      --success:   #1E7A4E;
      --radius:    10px;
      --shadow:    0 2px 16px rgba(11,31,64,.08), 0 1px 4px rgba(11,31,64,.06);
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--offwhite);
      font-family: 'DM Sans', sans-serif;
      color: var(--text);
    }

    /* ── Page wrapper ── */
    .eq-page {
      min-height: 100vh;
      background:
        radial-gradient(ellipse 70% 45% at 50% -10%, rgba(201,168,76,.12) 0%, transparent 70%),
        var(--offwhite);
      padding: 48px 16px 80px;
    }

    /* ── School Header ── */
    .eq-school-header {
      max-width: 760px;
      margin: 0 auto 24px;
      background: var(--surface);
      border-radius: 16px;
      border: 1px solid var(--border);
      box-shadow: var(--shadow);
      padding: 20px 28px;
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .eq-school-logo {
      width: 72px;
      height: 72px;
      object-fit: contain;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: #f5f5f3;
      flex-shrink: 0;
    }
    .eq-school-logo-placeholder {
      width: 72px;
      height: 72px;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: var(--navy);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 22px;
      color: var(--gold);
      font-family: 'DM Serif Display', serif;
    }
    .eq-school-info { flex: 1; min-width: 0; }
    .eq-school-name {
      font-family: 'DM Serif Display', serif;
      font-size: clamp(16px, 3vw, 22px);
      color: var(--navy);
      font-weight: 400;
      margin: 0 0 2px;
      line-height: 1.2;
    }
    .eq-school-branch {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: var(--gold);
      margin: 0 0 8px;
    }
    .eq-school-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 18px;
    }
    .eq-school-meta-item {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      color: var(--muted);
      font-weight: 400;
    }
    .eq-school-meta-item svg {
      flex-shrink: 0;
      opacity: .7;
    }
    .eq-school-header-loading {
      max-width: 760px;
      margin: 0 auto 24px;
      background: var(--surface);
      border-radius: 16px;
      border: 1px solid var(--border);
      padding: 20px 28px;
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .eq-skeleton {
      background: linear-gradient(90deg, #ece9e3 25%, #f5f3ee 50%, #ece9e3 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
      border-radius: 6px;
    }
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ── Enquiry badge strip ── */
    .eq-reg-badge {
      max-width: 760px;
      margin: 0 auto 16px;
      text-align: center;
    }
    .eq-reg-badge-pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--navy);
      color: var(--gold);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      padding: 6px 16px;
      border-radius: 100px;
    }
    .eq-reg-badge-pill::before {
      content: '';
      width: 6px; height: 6px;
      background: var(--gold);
      border-radius: 50%;
    }

    /* ── Card ── */
    .eq-card {
      max-width: 760px;
      margin: 0 auto;
      background: var(--surface);
      border-radius: 16px;
      box-shadow: var(--shadow);
      border: 1px solid var(--border);
      overflow: hidden;
    }

    /* ── Section blocks inside card ── */
    .eq-section {
      padding: 28px 36px;
      border-bottom: 1px solid var(--border);
    }
    .eq-section:last-child { border-bottom: none; }

    .eq-section-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .12em;
      text-transform: uppercase;
      color: var(--gold);
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .eq-section-label::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(90deg, var(--border) 0%, transparent 100%);
    }

    /* ── Field label ── */
    .eq-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .04em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 6px;
    }
    .eq-label .req {
      color: var(--error);
      margin-left: 2px;
    }

    /* ── MUI overrides ── */
    .eq-card .MuiOutlinedInput-root {
      border-radius: var(--radius) !important;
      background: #FAFAF8;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
    }
    .eq-card .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
      border-color: var(--border) !important;
    }
    .eq-card .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--gold) !important;
    }
    .eq-card .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--navy) !important;
      border-width: 2px !important;
    }
    .eq-card .MuiSelect-select {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
    }

    /* ── Buttons ── */
    .eq-btn-save {
      background: var(--navy) !important;
      color: #fff !important;
      font-family: 'DM Sans', sans-serif !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      letter-spacing: .06em !important;
      text-transform: uppercase !important;
      padding: 12px 32px !important;
      border-radius: var(--radius) !important;
      box-shadow: 0 4px 14px rgba(11,31,64,.25) !important;
      transition: all .2s ease !important;
    }
    .eq-btn-save:hover {
      background: var(--navy-mid) !important;
      box-shadow: 0 6px 20px rgba(11,31,64,.35) !important;
      transform: translateY(-1px);
    }
    .eq-btn-reset {
      background: transparent !important;
      color: var(--muted) !important;
      font-family: 'DM Sans', sans-serif !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      letter-spacing: .06em !important;
      text-transform: uppercase !important;
      padding: 12px 28px !important;
      border-radius: var(--radius) !important;
      border: 1.5px solid var(--border) !important;
      transition: all .2s ease !important;
    }
    .eq-btn-reset:hover {
      border-color: var(--error) !important;
      color: var(--error) !important;
      background: #fdf5f5 !important;
    }

    /* ── Footer strip inside card ── */
    .eq-footer-strip {
      background: #FAFAF8;
      border-top: 1px solid var(--border);
      padding: 20px 36px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
    }
    .eq-footer-note {
      font-size: 12px;
      color: var(--muted);
      font-weight: 300;
    }
    .eq-btn-row { display: flex; gap: 12px; flex-wrap: wrap; }

    /* ── Dialog ── */
    .eq-dialog-header {
      background: var(--navy);
      padding: 14px 24px;
    }
    .eq-dialog-header .MuiTypography-root {
      color: #fff;
      font-family: 'DM Serif Display', serif !important;
      font-weight: 400;
      font-size: 18px;
    }

    /* ── Responsive ── */
    @media (max-width: 600px) {
      .eq-section { padding: 20px 18px; }
      .eq-footer-strip { padding: 16px 18px; }
      .eq-btn-row { width: 100%; }
      .eq-btn-save, .eq-btn-reset { flex: 1; justify-content: center; }
      .eq-school-header { flex-direction: column; align-items: flex-start; padding: 16px 18px; gap: 14px; }
    }
  `}</style>
);

function StudentEnquiry() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [missingFields, setMissingFields] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purposes, setPurposes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(true);
  const [enquiryData, setEnquiryData] = useState({
    enquiryDate: new Date().toISOString().split('T')[0],
    purposeId: "",
    classId: "",
    contactNo: "",
    alternateMobile: "",
    studentName: "",
    parentAndGurdianName: "",
    motherName: "",
    emailId: "",
    refrence: "",
    remark: "",
  });
  const [mandatoryFieldsError, setMandatoryFieldsError] = useState(false);
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [enquiryExists, setEnquiryExists] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  /* ─── Fetch school info ──────────────────────────────────────────────────── */
  const fetchSchoolInfo = async () => {
    try {
      setSchoolLoading(true);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: "",
        }
      );
      if (!response.ok) throw new Error(`School fetch failed: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setSchoolInfo(data[0]);
      }
    } catch (error) {
      console.error("Error fetching school info:", error);
    } finally {
      setSchoolLoading(false);
    }
  };

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    setEnquiryData({
      enquiryDate: new Date().toISOString().split('T')[0],
      purposeId: "", classId: "", contactNo: "", alternateMobile: "",
      studentName: "", parentAndGurdianName: "", motherName: "",
      emailId: "", refrence: "", remark: "",
    });
  };

  const checkEnquiryByMobile = async (contactNo) => {
    const apiUrl = "https://wsmsv1.aidigitalsec.com/api/Enquiry/GetEnquiryByMobile";
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ contactNo }),
      });
      const responseData = await response.json();
      if (responseData && responseData.length > 0) {
        setEnquiryMessage("Enquiries found for this contact number. Contact School Administrator if you want to update existing enquiry.");
        setEnquiryExists(true);
      } else {
        setEnquiryMessage("No existing enquiry found. You may proceed.");
        setEnquiryExists(false);
      }
    } catch (error) {
      console.error("Error fetching enquiry:", error);
      setEnquiryMessage("Failed to check enquiry. Please try again.");
      setEnquiryExists(false);
    }
  };

  const handleContactChange = (e) => {
    const contactNo = e.target.value;
    if (/^\d{0,10}$/.test(contactNo)) {
      setEnquiryData({ ...enquiryData, contactNo });
      if (contactNo.length === 10) checkEnquiryByMobile(contactNo);
    }
  };

  const fetchEnquiryDetails = async (contactNo) => {
    const apiUrl = "https://wsmsv1.aidigitalsec.com/api/Enquiry/GetEnquiryByMobile";
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ contactNo }),
      });
      const enquiryDetails = await response.json();
      if (enquiryDetails.length > 0) {
        setEnquiryData(enquiryDetails[0]);
        handleModalOpen();
      }
    } catch (error) {
      console.error("Error fetching enquiry details:", error);
    }
  };

  const formatDate = (dateString) => {
    const dateParts = dateString.split("-");
    return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const mandatoryFields = [
      { name: 'Enquiry Date', value: enquiryData.enquiryDate },
      { name: 'Purpose', value: enquiryData.purposeId },
      { name: 'Class', value: enquiryData.classId },
      { name: 'Contact No.', value: enquiryData.contactNo },
      { name: 'Student Name', value: enquiryData.studentName },
      { name: 'Father Name', value: enquiryData.parentAndGurdianName },
    ];
    const missing = mandatoryFields.filter(field => !field.value);
    setMissingFields(missing.map(field => field.name));
    if (missing.length > 0) { setMandatoryFieldsError(true); return; }
    try {
      const payload = {
        enquiryCode: "",
        enquiryDate: formatDate(enquiryData.enquiryDate),
        followupDate: formatDate(new Date(new Date().setDate(
          new Date().getDate() + 2)).toISOString().split('T')[0]),
        purposeId: enquiryData.purposeId,
        classId: enquiryData.classId,
        contactNo: enquiryData.contactNo,
        alternateMobile: enquiryData.alternateMobile,
        studentName: enquiryData.studentName,
        parentAndGurdianName: enquiryData.parentAndGurdianName,
        motherName: enquiryData.motherName,
        emailId: enquiryData.emailId,
        refrence: enquiryData.refrence,
        remark: enquiryData.remark,
        modeId: 3,
      };
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Enquiry/InsertEnquirySchool",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setEnquiryData({
        enquiryDate: new Date().toISOString().split('T')[0],
        purposeId: "", classId: "", contactNo: "", alternateMobile: "",
        studentName: "", parentAndGurdianName: "", motherName: "",
        emailId: "", refrence: "", remark: "",
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      setSnackbarMessage('Failed to submit enquiry. Please try again later.');
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    fetchSchoolInfo();

    const fetchDropdownData = async () => {
      try {
        const apiUrl = "https://wsmsv1.aidigitalsec.com";
        const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
        const fetchData = async (endpoint, name) => {
          const response = await fetch(`${apiUrl}${endpoint}`, { method: "POST", headers, body: JSON.stringify({}) });
          if (!response.ok) throw new Error(`${name} fetch failed with status: ${response.status}`);
          return await response.json();
        };
        const [purposeData, classData] = await Promise.all([
          fetchData("/api/Enquiry/ddlPurpose", "Purpose"),
          fetchData("/api/Enquiry/ddlClassName", "Class"),
        ]);
        setPurposes(purposeData || []);
        setClasses(classData || []);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        setSnackbarMessage("Failed to load dropdown data.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    };
    fetchDropdownData();
  }, []);

  const handleReset = () => {
    setEnquiryData({
      enquiryDate: new Date().toISOString().split('T')[0],
      purposeId: "", classId: "", contactNo: "", alternateMobile: "",
      studentName: "", parentAndGurdianName: "", motherName: "",
      emailId: "", refrence: "", remark: "",
    });
    setMandatoryFieldsError(false);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: '#FAFAF8',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '14px',
    },
  };

  return (
    <>
      <GlobalStyles />
      <div className="eq-page">

        {/* ── School Header ── */}
        {schoolLoading ? (
          <div className="eq-school-header-loading">
            <div className="eq-skeleton" style={{ width: 72, height: 72, borderRadius: 10, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="eq-skeleton" style={{ height: 20, width: '60%', marginBottom: 8 }} />
              <div className="eq-skeleton" style={{ height: 13, width: '40%', marginBottom: 10 }} />
              <div className="eq-skeleton" style={{ height: 12, width: '75%' }} />
            </div>
          </div>
        ) : schoolInfo ? (
          <div className="eq-school-header">
            {schoolInfo.logoImgUrl ? (
              <img
                src={schoolInfo.logoImgUrl}
                alt={schoolInfo.schoolName}
                className="eq-school-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="eq-school-logo-placeholder">
                {schoolInfo.schoolName ? schoolInfo.schoolName.charAt(0) : "S"}
              </div>
            )}
            <div className="eq-school-info">
              <p className="eq-school-name">{schoolInfo.schoolName}</p>
              {schoolInfo.branchName && schoolInfo.branchName !== schoolInfo.schoolName && (
                <p className="eq-school-branch">{schoolInfo.branchName}</p>
              )}
              <div className="eq-school-meta">
                {schoolInfo.address && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    {schoolInfo.address}
                  </span>
                )}
                {schoolInfo.phoneNo && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.83a16 16 0 0 0 6.29 6.29l1.62-1.62a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.07z"/>
                    </svg>
                    {schoolInfo.phoneNo}
                  </span>
                )}
                {schoolInfo.affilationNo && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                    Affil. No: {schoolInfo.affilationNo}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Enquiry badge ── */}
        <div className="eq-reg-badge">
          <span className="eq-reg-badge-pill">Enquiry Form</span>
        </div>

        {/* ── Main Card ── */}
        <div className="eq-card">
          <form onSubmit={handleSubmit}>

            {/* ── Section 1: Enquiry Details ── */}
            <div className="eq-section">
              <div className="eq-section-label">Enquiry Details</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="enquiryDate">
                    Enquiry Date <span className="req">*</span>
                  </label>
                  <TextField
                    id="enquiryDate" type="date" fullWidth size="small"
                    value={enquiryData.enquiryDate}
                    onChange={(e) => setEnquiryData({ ...enquiryData, enquiryDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !enquiryData.enquiryDate &&
                    <FormHelperText error>Please select enquiry date</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="purpose">
                    Purpose <span className="req">*</span>
                  </label>
                  <Select id="purpose" fullWidth size="small" displayEmpty
                    value={enquiryData.purposeId}
                    onChange={(e) => setEnquiryData({ ...enquiryData, purposeId: e.target.value })}
                    sx={{ borderRadius: '10px', background: '#FAFAF8', fontFamily: "'DM Sans', sans-serif", fontSize: '14px' }}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select Purpose —</em></MenuItem>
                    {purposes.map((p) => <MenuItem key={p.purposeId} value={p.purposeId}>{p.purpose}</MenuItem>)}
                  </Select>
                  {mandatoryFieldsError && !enquiryData.purposeId &&
                    <FormHelperText error>Please select purpose</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="class">
                    Applying for Class <span className="req">*</span>
                  </label>
                  <Select id="class" fullWidth size="small" displayEmpty
                    value={enquiryData.classId}
                    onChange={(e) => setEnquiryData({ ...enquiryData, classId: e.target.value })}
                    sx={{ borderRadius: '10px', background: '#FAFAF8', fontFamily: "'DM Sans', sans-serif", fontSize: '14px' }}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select Class —</em></MenuItem>
                    {classes.map((c) => <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>)}
                  </Select>
                  {mandatoryFieldsError && !enquiryData.classId &&
                    <FormHelperText error>Please select class</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="contactNo">
                    Contact No. <span className="req">*</span>
                  </label>
                  <TextField id="contactNo" type="text" fullWidth size="small"
                    value={enquiryData.contactNo}
                    onChange={handleContactChange}
                    inputProps={{ maxLength: 10 }}
                    placeholder="10-digit mobile number"
                    sx={fieldSx}
                  />
                  {enquiryMessage && (
                    <FormHelperText style={{ color: enquiryExists ? "#C0392B" : "#1E7A4E", fontWeight: 500 }}>
                      {enquiryMessage}
                    </FormHelperText>
                  )}
                  {mandatoryFieldsError && !enquiryData.contactNo &&
                    <FormHelperText error>Please enter contact number</FormHelperText>}
                </Grid>
              </Grid>
            </div>

            {/* ── Section 2: Student & Family Details ── */}
            <div className="eq-section">
              <div className="eq-section-label">Student &amp; Family Details</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="studentName">
                    Student Name <span className="req">*</span>
                  </label>
                  <TextField id="studentName" type="text" fullWidth size="small"
                    value={enquiryData.studentName}
                    onChange={(e) => setEnquiryData({ ...enquiryData, studentName: e.target.value })}
                    placeholder="Full name of the student"
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !enquiryData.studentName &&
                    <FormHelperText error>Please enter student name</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="parentAndGurdianName">
                    Father / Guardian Name <span className="req">*</span>
                  </label>
                  <TextField id="parentAndGurdianName" type="text" fullWidth size="small"
                    value={enquiryData.parentAndGurdianName}
                    onChange={(e) => setEnquiryData({ ...enquiryData, parentAndGurdianName: e.target.value })}
                    placeholder="Father or guardian's name"
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !enquiryData.parentAndGurdianName &&
                    <FormHelperText error>Please enter father name</FormHelperText>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="motherName">Mother Name</label>
                  <TextField id="motherName" type="text" fullWidth size="small"
                    value={enquiryData.motherName}
                    onChange={(e) => setEnquiryData({ ...enquiryData, motherName: e.target.value })}
                    placeholder="Mother's name"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="alternateMobile">Alternate Number</label>
                  <TextField id="alternateMobile" type="text" fullWidth size="small"
                    value={enquiryData.alternateMobile}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => setEnquiryData({ ...enquiryData, alternateMobile: e.target.value })}
                    placeholder="Secondary contact (optional)"
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </div>

            {/* ── Section 3: Additional Info ── */}
            <div className="eq-section">
              <div className="eq-section-label">Additional Information</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="emailId">Email Address</label>
                  <TextField id="emailId" type="text" fullWidth size="small"
                    value={enquiryData.emailId}
                    onChange={(e) => setEnquiryData({ ...enquiryData, emailId: e.target.value })}
                    placeholder="youremail@example.com"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="refrence">Reference / Source</label>
                  <TextField id="refrence" type="text" fullWidth size="small"
                    value={enquiryData.refrence}
                    onChange={(e) => setEnquiryData({ ...enquiryData, refrence: e.target.value })}
                    placeholder="How did you hear about us?"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label className="eq-label" htmlFor="remark">Remarks</label>
                  <TextField id="remark" type="text" fullWidth size="small" multiline rows={3}
                    value={enquiryData.remark}
                    onChange={(e) => setEnquiryData({ ...enquiryData, remark: e.target.value })}
                    placeholder="Any additional information you'd like to share…"
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </div>

            {/* ── Footer strip ── */}
            <div className="eq-footer-strip">
              <span className="eq-footer-note">
                Fields marked <span style={{ color: 'var(--error)' }}>*</span> are required
              </span>
              <div className="eq-btn-row">
                <Button type="button" className="eq-btn-reset" onClick={handleReset} disableRipple>
                  Reset
                </Button>
                <Button type="submit" className="eq-btn-save" disableElevation>
                  Submit Enquiry
                </Button>
              </div>
            </div>

          </form>
        </div>
      </div>

      {/* ── View Details Modal ── */}
      <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box className="eq-dialog-header">
            <Typography>Existing Enquiry Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} mt={0}>
            {[
              { label: 'Enquiry Date', id: 'enquiryDate', value: enquiryData.enquiryDate },
              { label: 'Father Name', id: 'parentAndGurdianName', value: enquiryData.parentAndGurdianName },
              { label: 'Student Name', id: 'studentName', value: enquiryData.studentName },
              { label: 'Mother Name', id: 'motherName', value: enquiryData.motherName },
              { label: 'Email ID', id: 'emailId', value: enquiryData.emailId },
              { label: 'Alternate Mobile', id: 'alternateMobile', value: enquiryData.alternateMobile },
              { label: 'Reference', id: 'refrence', value: enquiryData.refrence },
              { label: 'Remark', id: 'remark', value: enquiryData.remark },
              { label: 'Class', id: 'classId', value: enquiryData.classId },
              { label: 'Purpose', id: 'purposeId', value: enquiryData.purposeId },
            ].map(({ label, id, value }) => (
              <Grid item xs={12} md={6} key={id}>
                <label className="eq-label" htmlFor={id}>{label}</label>
                <TextField id={id} type="text" fullWidth size="small" value={value || '—'}
                  InputProps={{ readOnly: true, sx: { background: '#f9f9f7' } }} sx={fieldSx} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--border)' }}>
          <Button onClick={handleModalClose} variant="outlined"
            sx={{ borderRadius: '10px', fontFamily: "'DM Sans',sans-serif", fontWeight: 600, textTransform: 'none', borderColor: 'var(--border)', color: 'var(--muted)', '&:hover': { borderColor: '#999' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success Modal ── */}
      <Dialog open={showSuccessModal} onClose={() => setShowSuccessModal(false)}
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', maxWidth: 420 } }}>
        <Box sx={{ backgroundColor: '#0B1F40', padding: '14px 20px' }}>
          <Typography sx={{ color: '#fff', fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '18px' }}>
            Enquiry Submitted ✓
          </Typography>
        </Box>
        <Box sx={{ padding: '24px 24px 20px' }}>
          <Typography sx={{ mb: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: '#1A1714' }}>
            Thank you! Your enquiry has been received.
          </Typography>
          <Typography sx={{ mb: 3, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#7A7267' }}>
            Our admissions team will review your details and get in touch with you shortly.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setShowSuccessModal(false); navigate(-1); }}
              variant="contained" disableElevation
              sx={{ background: '#0B1F40', borderRadius: '10px', fontFamily: "'DM Sans',sans-serif", fontWeight: 600, textTransform: 'none', px: 3, '&:hover': { background: '#132d5a' } }}>
              Done
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Missing Fields Modal ── */}
      <Dialog open={mandatoryFieldsError} onClose={() => setMandatoryFieldsError(false)}
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', maxWidth: 400 } }}>
        <Box sx={{ backgroundColor: '#0B1F40', padding: '14px 20px' }}>
          <Typography sx={{ color: '#fff', fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '18px' }}>
            Required Fields Missing
          </Typography>
        </Box>
        <Box sx={{ padding: '24px 24px 20px' }}>
          <Typography sx={{ mb: 2, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#7A7267' }}>
            Please fill in the following required fields to continue:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 3, '& li': { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#C0392B', mb: .5 } }}>
            {missingFields.map((field, index) => <li key={index}>{field}</li>)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMandatoryFieldsError(false)} variant="contained" disableElevation
              sx={{ background: '#0B1F40', borderRadius: '10px', fontFamily: "'DM Sans',sans-serif", fontWeight: 600, textTransform: 'none', px: 3, '&:hover': { background: '#132d5a' } }}>
              Got it
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default StudentEnquiry;